import { FC, JSX, useContext, useMemo, useRef } from "react";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { WafflesThemes, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { Waffles } from "@app/shared/waffles/Waffles";
import PageSection from "@app/shared/PageSection";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { GapImpactPaginationResult, HospitalService } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { NO_ACTIONS_APPLIED_LABEL, formatCountiesImpacted, formatGapImpactCount, formatScore, formatSpecialtyWithCode } from "@app/projects/network-assessment/provider-hub/utility";

interface HospitalServiceTableRow {
    [wafflesKeyColumnName]: string;
    name: string | JSX.Element;
    identifier: string;
    specialtiesImpacted: JSX.Element | string;
    providerSources: JSX.Element;
    providerActionStatus: JSX.Element | string;
    gapImpact: string | number;
    activeGapImpact: string | number;
    pendingGapImpact: string | number;
    approvedGapImpact: string | number;
    opportunityGapImpact: string | number;
    countiesImpacted: JSX.Element | string;
    accessPoints: string | number;
    effectiveness: string | number;
    efficiency: string | number;
    totalClaims: string | number;
    medicareClaims: string | number;
    medicaidClaims: string | number;
    commercialClaims: string | number;
}

interface HospitalServicesTableProps {
    hospitalServicesData: GapImpactPaginationResult<HospitalService>;
    pageIndex: number;
    pageSize: number;
    resetToFirstPage: boolean;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
    setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
}

const HospitalServicesTable: FC<HospitalServicesTableProps> = (props: HospitalServicesTableProps) => {
    const projectContext = useContext(CountyProjectContext);
    const providerHubContext = useContext(ProviderHubContext);
    const providerHubService = useRef(new ProviderHubService()).current;
    const showPci = projectContext.project.showPciData !== false;

    const useRegionStatusBreakdown = useMemo<boolean>(() => {
        return props.hospitalServicesData.useGapsByRegionStatus === true;
    }, [props.hospitalServicesData]);

    // Services are NPI type-2 organizations; the name links to that organization's profile (mirrors the
    // Organization search-grid navigation). Both the org and its contract entity are needed because the
    // Organization profile renders selectedContractEntity.
    const handleOrganizationClick = async (org: HospitalService) => {
        const projectId = projectContext?.project?.projectId;
        const prc = providerHubContext.selectedHospital.projectRunConfigurationId;
        const [organizationDetails, contractEntityDetails] = await Promise.all([
            providerHubService.getOrganizationDetails(projectId, prc, org.npi, org.contractEntityId),
            providerHubService.getContractEntityDetails(projectId, prc, org.contractEntityId)
        ]);
        providerHubContext.navigateToOrganizationProfile(organizationDetails, contractEntityDetails, ProviderProfileReferrerEnum.HospitalProfile);
    };

    /* oxlint-disable sort-keys */
    const columnConfig: ColumnConfig = {
        name: {
            displayText: "Name",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_hospital-services-table-name",
            noSort: true
        },
        identifier: {
            displayText: "Identifier",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_hospital-services-table-identifier",
            noSort: true
        },
        specialtiesImpacted: {
            displayText: "Specialty Groups Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-specialty",
            tooltip: "Indicates the Specialty Groups that the Organization services",
            noSort: true
        },
        providerSources: {
            displayText: "Provider Sources",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-provider-source",
            tooltip: "Indicates the original database from which the provider data was obtained",
            noSort: true
        },
        providerActionStatus: {
            displayText: "Provider Action Status",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-provider-action-status",
            tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for an organization",
            noSort: true
        },
        gapImpact: {
            displayText: "Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-gap-impact",
            tooltip: "Indicates the distinct number of gaps that the facility closes",
            noSort: true,
            hidden: useRegionStatusBreakdown
        },
        activeGapImpact: {
            displayText: "Active Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-active-gap-impact",
            tooltip: "Count of gaps closeable in Active service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        pendingGapImpact: {
            displayText: "Pending Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-pending-gap-impact",
            tooltip: "Count of gaps closeable in Pending service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        approvedGapImpact: {
            displayText: "Approved Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-approved-gap-impact",
            tooltip: "Count of gaps closeable in Approved service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        opportunityGapImpact: {
            displayText: "Opportunity Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-opportunity-gap-impact",
            tooltip: "Count of gaps closeable in Opportunity-to-expand service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        countiesImpacted: {
            displayText: "Counties Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-county",
            noSort: true
        },
        accessPoints: {
            displayText: "Access Points",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-access-points",
            noSort: true
        },
        effectiveness: {
            displayText: "Effectiveness (Quality)",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-effectiveness",
            hidden: !showPci,
            noSort: true
        },
        efficiency: {
            displayText: "Efficiency (Cost)",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-efficiency",
            hidden: !showPci,
            noSort: true
        },
        totalClaims: {
            displayText: "Total Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-total-claims",
            hidden: !showPci,
            noSort: true
        },
        medicareClaims: {
            displayText: "Medicare Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-medicare-claims",
            hidden: !showPci,
            noSort: true
        },
        medicaidClaims: {
            displayText: "Medicaid Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-medicaid-claims",
            hidden: !showPci,
            noSort: true
        },
        commercialClaims: {
            displayText: "Commercial Claims",
            sizeContentToFit: true,
            testId: "provider-hub_hospital-services-table-commercial-claims",
            hidden: !showPci,
            noSort: true
        }
    };
    /* oxlint-enable sort-keys */

    const handlePageSizeChange = (pageSize: number) => {
        props.setPageSize(pageSize);
        props.setPageIndex(1);
    };

    const handlePageChange = (newPage: number) => {
        if (!props.resetToFirstPage) {
            props.setPageIndex(newPage);
        }
        if (props.resetToFirstPage) {
            props.setResetToFirstPage(false);
        }
    };

    /* oxlint-disable sort-keys */
    const tableData = useMemo<HospitalServiceTableRow[]>(() => {
        return props.hospitalServicesData.items.map((org) => ({
            [wafflesKeyColumnName]: org.npi,
            name: org.contractEntityId
                ? <QESButton
                    qesType="link"
                    qesTestId={`provider-hub-hospital-services-table__organization--${org.npi}`}
                    onClick={() => { handleOrganizationClick(org) }}
                    className="demibold"
                >
                    {org.name ?? "---"}
                </QESButton>
                : (org.name ?? "---"),
            identifier: `NPI (Type 2): ${org.npi}`,
            specialtiesImpacted: <WaffleRowTooltip
                containerClassName="provider-hub-waffle-tooltip"
                elements={(org.filteredServicingSpecialties ?? []).map(formatSpecialtyWithCode)}
                numberOfElementsPerRow={3} />,
            providerSources: (
                <>
                    {sortProviderNetworkTypes(org.providerNetworkTypes ?? []).map((networkType) =>
                        <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
                    )}
                </>
            ),
            providerActionStatus: (org.providerActionStatuses ?? []).length > 0 ? (
                <>
                    {(org.providerActionStatuses ?? []).map((status, index) =>
                        <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />
                    )}
                </>
            ) : (
                <>{NO_ACTIONS_APPLIED_LABEL}</>
            ),
            gapImpact: formatGapImpactCount(org.gapsClosed),
            activeGapImpact: formatGapImpactCount(org.activeGapsClosed),
            pendingGapImpact: formatGapImpactCount(org.pendingGapsClosed),
            approvedGapImpact: formatGapImpactCount(org.approvedGapsClosed),
            opportunityGapImpact: formatGapImpactCount(org.opportunityGapsClosed),
            countiesImpacted: <WaffleRowTooltip
                containerClassName="provider-hub-waffle-tooltip"
                elements={formatCountiesImpacted(org.countiesImpacted)}
                numberOfElementsPerRow={3} />,
            accessPoints: org.accessPointCount ?? "---",
            effectiveness: formatScore(org.quality),
            efficiency: formatScore(org.cost),
            totalClaims: org.totalClaims ?? "---",
            medicareClaims: org.medicareClaims ?? "---",
            medicaidClaims: org.medicaidClaims ?? "---",
            commercialClaims: org.commercialClaims ?? "---"
        }));
    }, [props.hospitalServicesData]);
    /* oxlint-enable sort-keys */

    const paginationProps: WafflesPaginationProps = {
        changePage: (pageNumber: number) => handlePageChange(pageNumber),
        enableBackendPagination: true,
        handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
        pageSizeOptions: [50, 100, 150],
        recordsPerPage: props.pageSize,
        resetToFirstPage: props.resetToFirstPage,
        showPageSizeSelector: true,
        totalRecords: props.hospitalServicesData.totalCount
    };

    return (
        <PageSection>
            <Waffles<HospitalServiceTableRow>
                columns={columnConfig}
                data={tableData}
                totalNumberOfRecords={props.hospitalServicesData.totalCount}
                enablePagination={true}
                emptyTableMessage="No services found for this hospital."
                fullWidth={true}
                paginationProps={paginationProps}
                testId="provider-hub__hospital-services-table"
                theme={WafflesThemes.ContractEntityDetails}
                disableStickyHeader={true}
                variant="frozen-border"
            />
        </PageSection>
    );
};

export default HospitalServicesTable;
